import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DownloadLostAndFoundIssueWithCacheAsCsvResponse,
  LostAndFoundIssueWithCache,
  LostAndFoundIssueWithCacheSearch,
  QueryDownloadLostAndFoundIssueWithCacheAsCsvArgs,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { TypedDocumentNode } from '@apollo/client';
import { getProperty, queryInput } from '@/utils';
import graphApi from '@/api';
import { DownloadLostAndFoundIssueWithCacheAsCsvDocument } from '@/api/queries';

// Service Function Signature
type ServiceMethodPayload = ({
  startDatetime?: string;
  endDatetime?: string;
  tripAirportCodes?: string[];
  tripServicerIataAirlineCodes?: string[];
  returnStatuses?: string[];
} & {
  [key in keyof LostAndFoundIssueWithCacheSearch]?: any;
})[];

const lostAndFoundIssueCSVKeys: (keyof LostAndFoundIssueWithCache)[] = [
  'crewMemberEmail',
  'crewMemberName',
  'crewMemberPhone',
  'datetime',
  'description',
  'dispatcherFirstName',
  'dispatcherLastName',
  'driverFirstName',
  'driverLastName',
  'itemLost',
  'notes',
  'outboundDatetime',
  'outboundFlightNumber',
  'returnStatus',
  'shippingAddress',
  'shippingCity',
  'shippingCost',
  'shippingMethod',
  'shippingStateCode',
  'shippingZipCode',
  'trackingNumber',
  'tripAirportCode',
  'tripFlightNumber',
  'tripServicerIataAirlineCode',
  'tripScheduled',
  'vehicleTrackingId',
];

const lostAndFoundIssueCSVColumns: Record<keyof LostAndFoundIssueWithCache, true> = Object.fromEntries(
  lostAndFoundIssueCSVKeys.map((key) => [key, true])
) as Record<keyof LostAndFoundIssueWithCache, true>;

interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadLostAndFoundIssueWithCacheAsCsvResponse>;
}

// SearchLostAndFoundIssueDocument Service
export const { downloadLostAndFoundIssueWithCacheCsv, useDownloadLostAndFoundIssueWithCacheCsv } = ((): {
  downloadLostAndFoundIssueWithCacheCsv: ServiceMethod;
  useDownloadLostAndFoundIssueWithCacheCsv: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(DownloadLostAndFoundIssueWithCacheAsCsvDocument).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<DownloadLostAndFoundIssueWithCacheAsCsvResponse> => {
        const query: LostAndFoundIssueWithCacheSearch[] = payload?.map((item) => ({
          createdAt: queryInput.date([item.startDatetime, item.endDatetime]),
          tripAirportCode: queryInput.orNull(item?.tripAirportCodes),
          tripServicerIataAirlineCode: queryInput.orNull(item?.tripServicerIataAirlineCodes),
          returnStatus: queryInput.orNull(item?.returnStatuses),
        }));
        const args: QueryDownloadLostAndFoundIssueWithCacheAsCsvArgs = {
          input: {
            query,
            columns: lostAndFoundIssueCSVColumns,
          },
        };
        const opts = { merge: options?.merge ? 'downloadLostAndFoundIssueWithCacheAsCSV' : undefined };
        const res = await fn(args, opts);
        return getProperty('downloadLostAndFoundIssueWithCacheAsCSV', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    downloadLostAndFoundIssueWithCacheCsv: fetch,
    useDownloadLostAndFoundIssueWithCacheCsv: useService,
  };
})();
